import { useEffect, Fragment, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPenToSquare, faTableLayout, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";

// Strip tags, collapse whitespace, and truncate — turns a Stripo/HTML email
// body into a single short text snippet suitable for the picker row.
const PREVIEW_TRUNCATE = 140;
const stripAndTruncate = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    // Drop <style>, <script>, and comments first so their contents don't bleed in
    const cleaned = raw
        .replace(/<style[\s\S]*?<\/style>/gi, " ")
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<!--[\s\S]*?-->/g, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, " ")
        .trim();
    if (cleaned.length <= PREVIEW_TRUNCATE) return cleaned;
    return cleaned.slice(0, PREVIEW_TRUNCATE).trimEnd() + "…";
};

// Crude heuristic — anything starting with a doctype or <html> tag is a full
// HTML document (Stripo export). Such templates benefit from an iframe preview;
// short plaintext templates do not.
const looksLikeHtmlDocument = (raw) => /^\s*(<!DOCTYPE|<html\b)/i.test(raw || "");

export default function BookingTemplate({ handleSubmit, current_booking, platform, setFieldValue }) {
    const [templates, setTemplates] = useState([]);
    const [whatsappTemplates, setWhatsappTemplates] = useState([]);
    const [expandedPreview, setExpandedPreview] = useState(null); // template.id when an iframe preview is open
    const [templateCategories, setTemplateCategories] = useState([]);
    const [years, setYears] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [templateFilter, setTemplateFilter] = useState("all");
    const [aosRecords, setAosRecords] = useState([]);
    const [selectedAosUnit, setSelectedAosUnit] = useState("");
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState("");
    const [selectedAppointment, setSelectedAppointment] = useState("");
    const [linkedChats, setLinkedChats] = useState([]);
    const [selectedLinkedChat, setSelectedLinkedChat] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const searchInputRef = useRef(null);
    const showAos = process.env.REACT_APP_HIDE_CHAT_TABS === "true";

    // Track if any visible template needs each type of shortcode
    const [anyTemplateNeedsAppointment, setAnyTemplateNeedsAppointment] = useState(false);
    const [anyTemplateNeedsAos, setAnyTemplateNeedsAos] = useState(false);
    const [anyTemplateNeedsLinkedChat, setAnyTemplateNeedsLinkedChat] = useState(false);

    // Load AOS records related to the current booking
    useEffect(() => {
        if (showAos && current_booking?.id) {
            axios.get(`${process.env.REACT_APP_API_URL}aos_records/${current_booking?.id}`).then((response) => {
                const records = response.data?.aos_records || [];
                setAosRecords(Array.isArray(records) ? records : []);
                // Don't auto-select - leave empty by default
            }).catch((error) => {
                console.error("Failed to load AOS records", error);
                setAosRecords([]);
            });
        }
    }, [current_booking?.id, showAos]);

    // Load appointment data for appointment shortcode support
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}appointment_link_data`).then((response) => {
            setAppointments(response.data.appointments || []);
        }).catch(() => {
            console.error("Failed to load appointment data");
        });
    }, []);

    // Filter appointments when property changes
    useEffect(() => {
        if (selectedProperty) {
            const filtered = appointments.filter(
                (apt) => apt.property_id === parseInt(selectedProperty)
            );
            setFilteredAppointments(filtered);
            // Don't auto-select - leave empty by default
            setSelectedAppointment("");
        } else {
            setFilteredAppointments([]);
            setSelectedAppointment("");
        }
    }, [selectedProperty, appointments]);

    // Don't pre-select property - leave empty by default
    // useEffect(() => {
    //     if (current_booking?.property_id && appointments.length > 0) {
    //         setSelectedProperty(current_booking.property_id.toString());
    //     }
    // }, [current_booking?.property_id, appointments]);

    // Load linked chats for the current booking
    useEffect(() => {
        if (current_booking?.id) {
            axios.get(`${process.env.REACT_APP_API_URL}linked_chats/${current_booking.id}`).then((response) => {
                setLinkedChats(response.data);
                // Don't auto-select - leave empty by default
            }).catch(() => {
                console.error("Failed to load linked chats");
            });
        }
    }, [current_booking?.id]);

    // Helper: check if a template message contains specific shortcode patterns
    const needsAppointment = (message) => /\[appointment_/.test(message);
    const needsAos = (message) => /\[aos_/.test(message);
    const needsLinkedChat = (message) => /\[main_chat_name\]|\[main_chat_surname\]/.test(message);

    // Helper: determine if a template is disabled due to missing required fields
    const getTemplateMissingFields = (message) => {
        const missing = [];
        if (needsAppointment(message) && !selectedAppointment) missing.push("Appointment");
        if (needsAos(message) && !selectedAosUnit) missing.push("AOS Unit");
        if (needsLinkedChat(message) && !selectedLinkedChat) missing.push("Linked Chat");
        return missing;
    };

    // Load templates and years for selection
    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_URL}get_years`)
            .then((response) => {
                let dbYears = response.data;
                dbYears.push({ label: "All years", value: 0 });
                dbYears.sort((a, b) => a.value - b.value);
                setYears(dbYears);
            })
            .catch(() => alert("Year load failed"));

        axios.get(`${process.env.REACT_APP_API_URL}templates`).then((response) => {
            setTemplates(response.data);
            setTemplateCategories(Object.keys(response.data));
        });

        axios.get(`${process.env.REACT_APP_API_URL}whatsapp_templates`).then((response) => {
            console.log("WhatsApp Templates loaded:", response.data.length, "templates");
            console.log(
                "Sample template with variation:",
                response.data.find((t) => t.variation),
            );
            console.log("Sample template structure:", response.data[0]);
            setWhatsappTemplates(response.data);
        });
    }, []);

    // Filter templates based on the search term and type filter
    const filteredWhatsappTemplates = whatsappTemplates.filter((template) => {
        // Check filter type
        let matchesFilter = true;
        if (templateFilter === "carousel") {
            matchesFilter = template.header_type === 3 || template.variation !== null;
        } else if (templateFilter === "normal") {
            matchesFilter = template.header_type !== 3 && template.variation === null;
        } else if (templateFilter === "non-approved") {
            matchesFilter = !template.approved;
        }

        if (!matchesFilter) return false;

        // Then check search term
        if (searchTerm) {
            return template.title.toLowerCase().includes(searchTerm.toLowerCase()) || template.message.toLowerCase().includes(searchTerm.toLowerCase());
        }

        return true;
    });

    const filteredTemplates = templateCategories.reduce((acc, title) => {
        // Safety check: ensure templates[title] is an array before calling flatMap
        if (!Array.isArray(templates[title])) {
            acc[title] = [];
            return acc;
        }
        
        acc[title] = templates[title].flatMap((template) => {
            if (template.attach_aos === 1) {
                // Safety check: ensure aosRecords is an array before calling .map()
                if (!Array.isArray(aosRecords)) {
                    return [];
                }
                
                return aosRecords
                    .map((aos) => ({
                        ...template,
                        title: `${template.title} - ${aos.unit?.name}`, // Append AOS unit to the title
                        aosUnit: aos.unit_id, // Include aosUnit as a separate field
                    }))
                    .filter(
                        (aosTemplate) => aosTemplate.title.toLowerCase().includes(searchTerm.toLowerCase()) || aosTemplate["message_" + platform]?.toLowerCase().includes(searchTerm.toLowerCase()),
                    );
            }
            return template.title.toLowerCase().includes(searchTerm.toLowerCase()) || template["message_" + platform]?.toLowerCase().includes(searchTerm.toLowerCase())
                ? [{ ...template, aosUnit: null }]
                : [];
        });
        return acc;
    }, {});

    // Check if any filtered templates need specific shortcodes
    useEffect(() => {
        let hasAppointment = false;
        let hasAos = false;
        let hasLinkedChat = false;

        filteredWhatsappTemplates.forEach((template) => {
            if (needsAppointment(template.message)) hasAppointment = true;
            if (needsAos(template.message)) hasAos = true;
            if (needsLinkedChat(template.message)) hasLinkedChat = true;
        });

        setAnyTemplateNeedsAppointment(hasAppointment);
        setAnyTemplateNeedsAos(hasAos);
        setAnyTemplateNeedsLinkedChat(hasLinkedChat);
    }, [filteredWhatsappTemplates]);

    return (
        <>
            <div className="text-white border-primary bg-primary rounded-t-xl pb-1 pt-2 border-b-4 font-medium flex justify-center items-center h-8 px-2 relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    className="rounded-full flex items-center text-white hover:cursor-pointer"
                >
                    <span className="sr-only">Open templates</span>
                    <FontAwesomeIcon className="h-5 w-5" aria-hidden="true" icon={faTableLayout} />
                </button>
            </div>

            <Transition.Root show={isOpen} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={setIsOpen}>
                    <div className="flex items-center justify-center min-h-screen px-4 py-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                            afterEnter={() => searchInputRef.current?.focus()}
                        >
                            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-3xl max-h-[90vh] flex flex-col">
                                {/* Header with close button */}
                                <div className="flex items-center justify-between p-4 border-b bg-primary">
                                    <Dialog.Title className="text-lg font-medium text-white">
                                        Select Template
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        className="text-white hover:text-gray-200"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Search Input and Filter - Sticky */}
                                <div className="bg-white p-4 border-b shadow-sm flex-shrink-0">
                        <div className="flex gap-2">
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search templates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === ' ') {
                                        e.stopPropagation();
                                    }
                                }}
                                className="flex-1 p-2 border rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <select
                                value={templateFilter}
                                onChange={(e) => setTemplateFilter(e.target.value)}
                                className="p-2 border rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="all">All</option>
                                <option value="normal">Normal</option>
                                <option value="carousel">Carousel</option>
                                <option value="non-approved">Non-Approved</option>
                            </select>
                        </div>
                        {/* Appointment & AOS selectors for shortcode-dependent templates */}
                        <div className="flex gap-2 mt-2">
                            {anyTemplateNeedsAppointment && (
                                <>
                                    <div className="flex-1">
                                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Property</label>
                                        <select
                                            value={selectedProperty}
                                            onChange={(e) => setSelectedProperty(e.target.value)}
                                            className="w-full p-1.5 border rounded-md text-gray-900 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                        >
                                            <option value="">-- Select Property --</option>
                                            {[...new Map(appointments.map(a => [a.property_id, { id: a.property_id, name: a.property_name }])).values()].map((prop) => (
                                                <option key={prop.id} value={prop.id}>{prop.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Appointment Type</label>
                                        <select
                                            value={selectedAppointment}
                                            onChange={(e) => setSelectedAppointment(e.target.value)}
                                            disabled={!selectedProperty}
                                            className="w-full p-1.5 border rounded-md text-gray-900 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                                        >
                                            <option value="">-- Select Appointment Type --</option>
                                            {filteredAppointments.map((apt) => (
                                                <option key={apt.id} value={apt.id}>{apt.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            {showAos && aosRecords.length > 0 && (
                                <div className="flex-1">
                                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">AOS Unit</label>
                                    <select
                                        value={selectedAosUnit}
                                        onChange={(e) => setSelectedAosUnit(e.target.value)}
                                        className="w-full p-1.5 border rounded-md text-gray-900 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    >
                                        <option value="">-- Select AOS Unit --</option>
                                        {aosRecords.map((aos) => (
                                            <option key={aos.id} value={aos.unit_id}>
                                                {aos.unit?.name || `Unit ${aos.unit_id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {anyTemplateNeedsLinkedChat && linkedChats.length > 0 && (
                                <div className="flex-1">
                                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Linked Chat</label>
                                    <select
                                        value={selectedLinkedChat}
                                        onChange={(e) => setSelectedLinkedChat(e.target.value)}
                                        className="w-full p-1.5 border rounded-md text-gray-900 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    >
                                        <option value="">-- Select Linked Chat --</option>
                                        {linkedChats.map((link) => {
                                            const linkedBooking = link.linked_booking_id === current_booking.id ? link.booking : link.linked_booking;
                                            const linkedUser = link.linked_booking_id === current_booking.id ? link.user : link.linked_user;
                                            const linkType = link.linked_booking_id === current_booking.id ? link.type : link.linked_type;
                                            return (
                                                <option key={link.id} value={linkedBooking?.id || ""}>
                                                    {linkType}: {linkedUser?.name || "Unknown"} {linkedUser?.surname || ""}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                                {/* Scrollable template content */}
                                <div className="overflow-y-auto flex-1">
                    {platform === "whatsapp" && (
                        <div>
                            {years &&
                                years.length > 0 &&
                                years.map((year) => {
                                    const yearTemplates = filteredWhatsappTemplates.filter(t => t.year_id === year.value);
                                    if (yearTemplates.length === 0) return null;
                                    
                                    return (
                                    <Fragment key={year.value}>
                                        <p className="text-center p-4 bg-dark_background text-white focus:outline-none">{year.label}</p>
                                        {yearTemplates.map((template) => {
                                                const missingFields = getTemplateMissingFields(template.message);
                                                const isDisabled = missingFields.length > 0;
                                                return (
                                                    <button
                                                        key={template.id}
                                                        type="button"
                                                        disabled={isDisabled}
                                                        className={`w-full text-left block px-4 py-2 text-sm ${isDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-200 hover:border-gray-200"}`}
                                                        onClick={async () => {
                                                            if (isDisabled) return;
                                                            setFieldValue("template", template.id);
                                                            setFieldValue("platform", "whatsapp");
                                                            setFieldValue("message", template.message);
                                                            setFieldValue("template_name", template.title);
                                                            if (selectedAosUnit && needsAos(template.message)) {
                                                                setFieldValue("aos_unit", selectedAosUnit);
                                                            }
                                                            if (selectedAppointment && needsAppointment(template.message)) {
                                                                setFieldValue("appointment_id", selectedAppointment);
                                                            }
                                                            if (selectedLinkedChat && needsLinkedChat(template.message)) {
                                                                setFieldValue("linked_chat_id", selectedLinkedChat);
                                                            }
                                                            setIsOpen(false);
                                                            
                                                            // Auto-send if template is approved
                                                            if (template.approved) {
                                                                setTimeout(() => {
                                                                    handleSubmit();
                                                                }, 100);
                                                            }
                                                        }}
                                                    >
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                {template.variation ? (
                                                                    <>
                                                                        <p className="text-left font-medium text-black text-sm">
                                                                            {template.variation.type === "PROPERTY_LIST"
                                                                                ? `${template.variation.items ? template.variation.items.join(", ") : "Properties"}`
                                                                                : template.variation.type === "PROPERTY_UNIT_TYPES"
                                                                                  ? `${template.variation.property_name ? template.variation.property_name + " - " : ""}${template.variation.items ? template.variation.items.join(", ") : "Unit Types"}`
                                                                                  : template.variation.type === "ALL_PROPERTIES"
                                                                                    ? `All Properties${template.variation.items ? ` (${template.variation.items.length})` : ""}`
                                                                                    : template.variation.type === "ALL_UNIT_TYPES_PER_PROPERTY"
                                                                                      ? `${template.variation.property_name || "All Unit Types"}`
                                                                                      : template.title}
                                                                        </p>
                                                                        <span className="bg-purple-100 text-purple-800 border-purple-200 border inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium">
                                                                            {template.variation.type === "PROPERTY_LIST" || template.variation.type === "ALL_PROPERTIES"
                                                                                ? "🏢 Property"
                                                                                : "🏠 Unit Type"}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <p className="text-left font-medium text-black text-sm">{template.title}</p>
                                                                )}
                                                                <span
                                                                    className={`${
                                                                        template.approved ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-700 border-gray-200"
                                                                    } border inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium`}
                                                                >
                                                                    {template.approved ? "Approved" : "Disabled"}
                                                                </span>
                                                                {isDisabled && (
                                                                    <span className="bg-amber-100 text-amber-800 border-amber-200 border inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium">
                                                                        Needs: {missingFields.join(", ")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="w-full text-gray-500 text-xs">{template.message}</p>
                                                        </button>
                                                );
                                            })}
                                    </Fragment>
                                    );
                                })}
                        </div>
                    )}
                    {templateCategories &&
                        templateCategories.map(
                            (title) =>
                                filteredTemplates[title].length > 0 && (
                                    <div key={`template_${title}`}>
                                        <p className="text-center p-4 bg-dark_background text-white focus:outline-none">{title}</p>
                                        {filteredTemplates[title].map((template) => {
                                            const body = template["message_" + platform];
                                            if (!body || body === "null" || body === "") {
                                                return <div key={template.id}></div>;
                                            }

                                            const isHtml = looksLikeHtmlDocument(body) || (platform === "email" && template.email_format === "html");
                                            const rowKey = `${template.id}_${template.aosUnit || ""}`;
                                            const isExpanded = expandedPreview === rowKey;

                                            const selectThis = () => {
                                                setFieldValue("message", body);
                                                setFieldValue("files", template.files ? JSON.parse(template.files) : []);
                                                setFieldValue("template", template.id);
                                                setFieldValue("template_name", template.title);
                                                setFieldValue("aos_unit", template.aosUnit);
                                                setIsOpen(false);
                                            };

                                            return (
                                                <div key={rowKey} className="border-b last:border-b-0">
                                                    <button
                                                        type="button"
                                                        className="w-full text-left hover:bg-blue-200 hover:border-blue-200 block px-4 py-2 text-sm"
                                                        onClick={selectThis}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <p className="font-medium text-black text-sm">{template.title}</p>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                {isHtml && (
                                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800">
                                                                        HTML
                                                                    </span>
                                                                )}
                                                                {isHtml && (
                                                                    <span
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setExpandedPreview(isExpanded ? null : rowKey);
                                                                        }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter" || e.key === " ") {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                setExpandedPreview(isExpanded ? null : rowKey);
                                                                            }
                                                                        }}
                                                                        className="text-xs text-indigo-700 hover:underline cursor-pointer flex items-center gap-1"
                                                                        title={isExpanded ? "Hide preview" : "Preview rendered HTML"}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                                                                        {isExpanded ? "Hide" : "Preview"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="w-full text-gray-500 text-xs mt-0.5 line-clamp-2">
                                                            {stripAndTruncate(body)}
                                                        </p>
                                                    </button>
                                                    {isExpanded && (
                                                        <div className="px-4 pb-3 bg-gray-50" onClick={(e) => e.stopPropagation()}>
                                                            <iframe
                                                                title={`Preview of ${template.title}`}
                                                                sandbox=""
                                                                srcDoc={body}
                                                                className="w-full h-96 border border-gray-200 rounded bg-white"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ),
                        )}
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>
        </>
    );
}
